import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import AuthLayout from '@/components/auth/AuthLayout';
import { Alert, AlertDescription } from '@/components/ui/alert';

const Verify = () => {
  const [searchParams] = useSearchParams(); // Keep for potential future use
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>({});
  const navigate = useNavigate();

  useEffect(() => {
    const currentUrl = window.location.href;
    const urlParamsForDebug = new URLSearchParams(window.location.search);
    const hashParamsForDebug = new URLSearchParams(window.location.hash.replace('#', ''));

    const hasCode = urlParamsForDebug.has('code');
    const hasToken = urlParamsForDebug.has('token');
    const hasType = urlParamsForDebug.has('type');
    const hasHashCode = hashParamsForDebug.has('code');
    const hasHashToken = hashParamsForDebug.has('token');
    const hasHashType = hashParamsForDebug.has('type');
    const tokenValue = urlParamsForDebug.get('token') || hashParamsForDebug.get('token') || urlParamsForDebug.get('code') || hashParamsForDebug.get('code') || '';

    setDebugInfo({
      url: currentUrl,
      hasCode,
      hasToken,
      hasType,
      hasHashCode,
      hasHashToken,
      hasHashType,
      codePreview: hasCode ? `${urlParamsForDebug.get('code')?.substring(0, 5)}...` : null,
      hashCodePreview: hasHashCode ? `${hashParamsForDebug.get('code')?.substring(0, 5)}...` : null,
      tokenFull: tokenValue,
      tokenPreview: tokenValue ? `${tokenValue.substring(0, 5)}...` : null,
      hashTokenPreview: hasHashToken ? `${hashParamsForDebug.get('token')?.substring(0, 5)}...` : null,
      type: urlParamsForDebug.get('type') || hashParamsForDebug.get('type'),
      rawSearch: window.location.search,
      rawHash: window.location.hash,
      timestamp: new Date().toISOString()
    });

    if ((!hasCode && hasHashCode) || (!hasToken && hasHashToken) || (!hasType && hasHashType)) {
      const newParams = new URLSearchParams(window.location.search);
      if (hasHashCode && !hasCode) newParams.set('code', hashParamsForDebug.get('code') || '');
      if (hasHashToken && !hasToken) newParams.set('token', hashParamsForDebug.get('token') || '');
      if (hasHashType && !hasType) newParams.set('type', hashParamsForDebug.get('type') || '');
      
      const currentPath = window.location.pathname;
      const newUrl = `${window.location.origin}${currentPath}${newParams.toString() ? '?' + newParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      // No reload, let main verification logic handle it after state update
    }
  }, []);

  const extractVerificationParams = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));

    let verificationToken = urlParams.get('token') || hashParams.get('token');
    if (!verificationToken) {
      verificationToken = urlParams.get('code') || hashParams.get('code');
    }
    const verificationType = urlParams.get('type') || hashParams.get('type');
    
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const typeFromHash = hashParams.get('type');

    console.log('Verify: Extracted parameters', {
      token: verificationToken ? `***${verificationToken.slice(-6)}` : 'none',
      type: verificationType,
      accessToken: accessToken ? 'present' : 'none',
      refreshToken: refreshToken ? 'present' : 'none',
      typeFromHash: typeFromHash,
      href: window.location.href
    });

    return { verificationToken, verificationType, accessToken, refreshToken, typeFromHash };
  }, []);

  const handleVerificationSuccess = useCallback(() => {
    console.log('Verification successful, cleaning up...');
    localStorage.removeItem('email_confirmation_pending');
    localStorage.removeItem('auth_flow');
    localStorage.removeItem('pending_email');
    setSuccess(true);
    setLoading(false);
  }, []);

  const handleVerificationError = useCallback((errorMessage: string, detailedError?: any) => {
    console.error('Verification error:', errorMessage, detailedError);
    let displayMessage = 'An error occurred during email verification. The link may be invalid, expired, or already used.';
    if (errorMessage.toLowerCase().includes('already been verified') || (detailedError && String(detailedError).toLowerCase().includes('already confirmed'))) {
        displayMessage = 'This email address has already been verified. You can proceed to login.';
        setSuccess(true); // Treat as success for UI if already verified
    } else if (errorMessage.toLowerCase().includes('expired')) {
        displayMessage = 'The verification link has expired. Please request a new one.';
    } else if (errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('invalid')) {
        displayMessage = 'The verification link is invalid or not found. Please check the link or request a new one.';
    }
    setError(displayMessage);
    setLoading(false);
  }, []);

  useEffect(() => {
    const verifyEmail = async () => {
      setLoading(true);
      setError(null);

      console.log('Verify: Starting email verification process');
      const { verificationToken, verificationType, accessToken, refreshToken, typeFromHash } = extractVerificationParams();

      setDebugInfo((prev: any) => ({
        ...prev,
        extractedTokenPreview: verificationToken ? `${verificationToken.substring(0,5)}...` : 'None',
        extractedType: verificationType || typeFromHash || 'Not specified',
        verificationAttemptTime: new Date().toISOString()
      }));

      // Handle Supabase's new OAuth/Magic Link flow (tokens in hash)
      if (accessToken && (typeFromHash === 'signup' || typeFromHash === 'magiclink' || typeFromHash === 'recovery')) {
        console.log('Verify: Found access token in hash, attempting to set session for type:', typeFromHash);
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '' // refreshToken might not always be present
        });

        if (sessionError) {
          console.error('Error setting session from hash tokens:', sessionError);
          handleVerificationError('Failed to verify your email using link. Please try again.', sessionError.message);
          return;
        }
        console.log('Verify: Successfully set session from hash tokens.');
        handleVerificationSuccess();
        return;
      }

      // Traditional token/code verification
      if (!verificationToken) {
        // Check if already verified (e.g. user clicks link again)
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.email_confirmed_at) {
            console.log('User already has a session and email is confirmed.');
            handleVerificationSuccess();
            return;
        }
        handleVerificationError('Verification token not found in the URL.');
        return;
      }

      try {
        console.log(`Verify: Attempting traditional verification with token (type: ${verificationType || 'unknown'})`);
        const { data, error: otpError } = await supabase.auth.verifyOtp({
          token: verificationToken,
          type: verificationType as any, // Cast as any to pass string type like 'signup', 'email_change', etc.
        });

        if (otpError) {
          console.error('Error verifying OTP (token):', otpError);
          if (otpError.message.toLowerCase().includes('already confirmed') || otpError.message.toLowerCase().includes('already verified')) {
            handleVerificationError('This email address has already been verified.', otpError.message);
          } else {
            handleVerificationError(otpError.message || 'Failed to verify email with token.', otpError.message);
          }
          return;
        }

        console.log('Verify: OTP/Token verification successful.', data);
        // Check various success conditions
        if (verificationType === 'recovery' || (data.session && data.user)) {
            console.log('Recovery or session established, verification successful.');
        } else if (data.user && data.user.email_confirmed_at) {
            console.log('User email confirmed.');
        } else if (data.user && !data.user.email_confirmed_at && verificationType === 'signup'){
            console.warn('Signup verification, user exists but email_confirmed_at not set. Waiting briefly.');
            await new Promise(resolve => setTimeout(resolve, 1500)); // Wait for potential replication delay
            const { data: { user: updatedUser } } = await supabase.auth.getUser();
            if (updatedUser?.email_confirmed_at) {
                console.log('Email confirmed after delay.');
            } else {
                console.warn('Email still not confirmed after delay. Proceeding as success if user object exists.');
            }
        } else {
            console.log('Verification successful, but no immediate session or specific confirmation state. Assuming email confirmed.');
        }
        handleVerificationSuccess();

      } catch (e: any) {
        console.error('Unexpected error during traditional verification process:', e);
        handleVerificationError('An unexpected error occurred.', e.toString());
      }
    };

    verifyEmail();
  }, [extractVerificationParams, handleVerificationSuccess, handleVerificationError]); // Add dependencies

  const handleContinue = () => {
    navigate('/login', {
      state: { 
        message: success ? 'Email verified successfully. Please log in.' : 'Returning to login.',
        variant: success ? 'success' : 'info' 
      },
      replace: true
    });
  };

  return (
    <AuthLayout 
      title="Email Verification"
      description="Verifying your email address"
    >
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-xl text-center">Email Verification</CardTitle>
          <CardDescription className="text-center">
            {loading ? 'Processing your verification...' : 
             success ? 'Your email has been verified!' : 
             error ? 'Verification Problem' : 'Checking verification status...'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6 py-8 px-6">
          {loading && (
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="h-16 w-16 animate-spin text-coaching-primary mb-4" />
              <p className="text-center text-muted-foreground">
                Please wait while we verify your email address...
              </p>
            </div>
          )}

          {!loading && success && (
            <div className="flex flex-col items-center justify-center">
              <div className="bg-green-100 dark:bg-green-900 p-4 rounded-full mb-4">
                <CheckCircle className="h-16 w-16 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-green-700 dark:text-green-300 mb-2">Verification Successful!</h3>
              <p className="text-center text-muted-foreground mb-6">
                Your email has been successfully verified. You can now log in to your account.
              </p>
              <Button onClick={handleContinue} className="w-full bg-coaching-primary hover:bg-coaching-primary/90">
                Continue to Login
              </Button>
            </div>
          )}

          {!loading && error && !success && ( // Ensure error only shows if not also success (e.g. already verified case)
            <div className="flex flex-col items-center justify-center">
              <div className="bg-red-100 dark:bg-red-900 p-4 rounded-full mb-4">
                <AlertCircle className="h-16 w-16 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-red-700 dark:text-red-300 mb-2">Verification Failed</h3>
              
              <Alert variant="destructive" className="mb-4 w-full">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              
              <p className="text-center text-muted-foreground mb-4">
                This can happen if the link has expired or was already used.
              </p>
              
              <div className="flex flex-col space-y-2 w-full">
                <Button onClick={handleContinue} className="w-full bg-coaching-primary hover:bg-coaching-primary/90">
                  Return to Login
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/auth-troubleshoot')}
                  className="w-full"
                >
                  Troubleshoot
                </Button>
              </div>
            </div>
          )}
          
          {/* Debugging information collapsible section */}
          {!loading && (
            <details className="mt-8 w-full text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View Debugging Information</summary>
              <div className="mt-2 p-3 border rounded bg-muted/50 max-h-60 overflow-auto space-y-1">
                <p><strong>Time:</strong> {debugInfo.timestamp}</p>
                <p><strong>URL:</strong> {debugInfo.url}</p>
                <p><strong>Path:</strong> {typeof window !== 'undefined' ? window.location.pathname : 'N/A'}</p>
                <p><strong>Search:</strong> {debugInfo.rawSearch}</p>
                <p><strong>Hash:</strong> {debugInfo.rawHash}</p>
                <p><strong>Token Preview (URL/Hash):</strong> {debugInfo.tokenPreview || 'None'}</p>
                <p><strong>Type (URL/Hash):</strong> {debugInfo.type || 'Not specified'}</p>
                <p><strong>Extracted Token Preview:</strong> {debugInfo.extractedTokenPreview || 'None'}</p>
                <p><strong>Extracted Type:</strong> {debugInfo.extractedType || 'Not specified'}</p>
                <p><strong>Verification Attempt Time:</strong> {debugInfo.verificationAttemptTime || 'N/A'}</p>
                <p><strong>localStorage auth_flow:</strong> {typeof window !== 'undefined' ? localStorage.getItem('auth_flow') || 'not set' : 'N/A'}</p>
                <p><strong>localStorage email_confirmation_pending:</strong> {typeof window !== 'undefined' ? localStorage.getItem('email_confirmation_pending') || 'not set' : 'N/A'}</p>
              </div>
            </details>
          )}
        </CardContent>
        
        <CardFooter className="flex justify-center border-t pt-4 mt-4">
          <p className="text-xs text-muted-foreground">
            If you continue to experience issues, please contact support.
          </p>
        </CardFooter>
      </Card>
    </AuthLayout>
  );
};

export default Verify;